import { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { NodeData } from '../types';
import { getNodeColor } from '../utils/graphBuilder';

/**
 * Custom ReactFlow node component for displaying Q&A pairs
 */
function QANodeComponent({ data }: NodeProps<NodeData>) {
  const { qaPair, hasConflict, conflicts } = data;
  const { question, answer } = qaPair;

  // Special styling for SUCCESS_CRITERIA root node
  const isRootNode = question.id === 'SUCCESS_CRITERIA';

  const depthColor = isRootNode ? '#f59e0b' : getNodeColor(question.depth);
  const borderColor = hasConflict ? '#ef4444' : depthColor;
  const backgroundColor = hasConflict
    ? '#fee2e2'
    : isRootNode
    ? '#fffbeb'
    : '#ffffff';

  return (
    <div
      style={{
        background: backgroundColor,
        border: `3px solid ${borderColor}`,
        borderRadius: isRootNode ? '12px' : '8px',
        padding: isRootNode ? '16px' : '12px',
        minWidth: isRootNode ? '300px' : '250px',
        maxWidth: isRootNode ? '400px' : '350px',
        fontSize: '12px',
        boxShadow: hasConflict
          ? '0 4px 6px -1px rgba(239, 68, 68, 0.3)'
          : isRootNode
          ? '0 8px 16px -2px rgba(245, 158, 11, 0.4)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Handles for connections */}
      {!isRootNode && <Handle type="target" position={Position.Top} />}

      {/* Header: Breadcrumb + Depth Badge */}
      <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: isRootNode ? '12px' : '10px', color: '#64748b', fontWeight: isRootNode ? 'bold' : 500 }}>
          {isRootNode ? '🎯 ' : ''}{question.breadcrumb}
        </span>
        <span
          style={{
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#ffffff',
            background: depthColor,
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          {isRootNode ? 'ROOT' : question.depth}
        </span>
      </div>

      {/* Question */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '2px' }}>
          QUESTION [{question.id}]
        </div>
        <div style={{ fontWeight: 'bold', color: '#1e293b', lineHeight: '1.3' }}>
          {question.questionText}
        </div>
      </div>

      {/* Answer */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '2px' }}>ANSWER</div>
        <div style={{ color: '#475569', lineHeight: '1.4' }}>
          {answer.answerText.length > 150
            ? answer.answerText.substring(0, 150) + '...'
            : answer.answerText}
        </div>
      </div>

      {/* Dependencies */}
      {answer.dependencies.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '2px' }}>DEPENDENCIES</div>
          <div style={{ fontSize: '10px', color: '#64748b' }}>
            {answer.dependencies.map((dep, idx) => (
              <span key={idx}>
                {dep.targetId}
                {idx < answer.dependencies.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Conflict Indicator */}
      {hasConflict && (
        <div
          style={{
            marginTop: '8px',
            padding: '6px',
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: '4px',
            fontSize: '10px',
            color: '#dc2626',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            ⚠️ {conflicts.length} Conflict{conflicts.length > 1 ? 's' : ''} Detected
          </div>
          {conflicts.map((conflict, idx) => (
            <div key={idx} style={{ fontSize: '9px', marginTop: '2px' }}>
              • {conflict.type === 'multiple' ? 'Multiple Dependencies' : 'Circular Dependency'}
            </div>
          ))}
        </div>
      )}

      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

export default memo(QANodeComponent);
